import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, getFileUrl } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/layout/Header';
import { Button } from '../../components/common/Common';
import MediaUploader from '../../components/common/MediaUploader';
import {
  Save, ArrowLeft, ChevronDown, Info, MapPin, DollarSign, Home,
  User, Sparkles, Camera, Trash2, Star, FileText, Film, Search, Check
} from 'lucide-react';
import './AddProperty.css';

const AMENITIES = ['Swimming Pool', 'Gym', 'Parking', 'Security', 'Garden', 'Clubhouse', 'Lift', 'Power Backup', 'CCTV', 'Play Area', 'Intercom', 'Fire Safety', 'Gas Pipeline', 'Rain Water Harvesting'];

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ab-section">
      <div
        className="ab-section__header"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
      >
        <h3 className="ab-section__title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          {Icon && <Icon size={16} />}
          {title}
        </h3>
        <ChevronDown
          size={18}
          style={{
            color: 'var(--gray-500)',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
      </div>
      {open && <div className="ab-section__body" style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
};

// Single-select with search + grouped options. Reuses the global
// `multi-select__*` styles (see Common.css) so it matches the rest of the app.
// `groups` = [{ label, options: [{ value, label }] }]; value/onChange are plain
// strings (tagged `proj:<id>` / `lp:<id>` here).
const SearchableSelect = ({ value, onChange, groups = [], placeholder = 'Select...', clearLabel = '-- None --' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selectedLabel = useMemo(() => {
    for (const g of groups) {
      const hit = g.options.find(o => String(o.value) === String(value));
      if (hit) return hit.label;
    }
    return '';
  }, [groups, value]);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(g => ({ ...g, options: g.options.filter(o => o.label.toLowerCase().includes(q)) }))
      .filter(g => g.options.length > 0);
  }, [groups, query]);

  const pick = (v) => { onChange(v); setOpen(false); setQuery(''); };

  return (
    <div className={`multi-select ${open ? 'multi-select--open' : ''}`} ref={wrapRef}>
      <button type="button" className="multi-select__control" onClick={() => setOpen(o => !o)}>
        <div className="multi-select__chips">
          {selectedLabel
            ? <span className="ss-single__value">{selectedLabel}</span>
            : <span className="multi-select__placeholder">{placeholder}</span>}
        </div>
        <ChevronDown size={16} className="multi-select__chev" />
      </button>
      {open && (
        <div className="multi-select__menu">
          <div className="multi-select__search">
            <Search size={13} />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search projects..." autoFocus />
          </div>
          <div className="multi-select__list">
            <button type="button" className={`multi-select__option ${!value ? 'multi-select__option--checked' : ''}`} onClick={() => pick('')}>
              <span className="multi-select__label">{clearLabel}</span>
              {!value && <Check size={14} style={{ color: 'var(--navy-500)' }} />}
            </button>
            {filteredGroups.length === 0 ? (
              <div className="multi-select__empty">No projects match</div>
            ) : (
              filteredGroups.map(g => (
                <div key={g.label}>
                  <div className="ss-group__label">{g.label}</div>
                  {g.options.map(o => {
                    const checked = String(o.value) === String(value);
                    return (
                      <button type="button" key={o.value} className={`multi-select__option ${checked ? 'multi-select__option--checked' : ''}`} onClick={() => pick(o.value)}>
                        <span className="multi-select__label">{o.label}</span>
                        {checked && <Check size={14} style={{ color: 'var(--navy-500)' }} />}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const detectKind = (m) => {
  const t = (m.media_type || '').toLowerCase();
  const url = m.file_url || m.url || '';
  const name = (m.file_name || m.name || '').toLowerCase();
  if (t === 'image' || t === 'floor_plan' || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (t === 'video' || /\.(mp4|webm|mov|avi|mkv)$/i.test(url) || /\.(mp4|webm|mov|avi|mkv)$/i.test(name)) return 'video';
  if (t === 'brochure' || t === 'document' || /\.pdf$/i.test(url) || /\.pdf$/i.test(name)) return 'pdf';
  return 'file';
};

const AddProperty = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [projects, setProjects] = useState([]);
  const [leadProjects, setLeadProjects] = useState([]);
  const [towers, setTowers] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  const [form, setForm] = useState({
    title: '', description: '', property_type: '', configuration: '',
    location: '', address: '', price: '', price_unit: 'INR',
    area: '', area_unit: 'sqft', bedrooms: '', bathrooms: '',
    floor: '', total_floors: '', furnishing: '', facing: '',
    amenities: '', status: 'available', project_id: '', source_lp_id: '',
    // New fields
    unit_number: '', tower_id: '',
    carpet_area: '', builtup_area: '', super_builtup_area: '',
    base_price: '',
    owner_type: '', owner_name: '', owner_contact: '',
    parking: '', balconies: '', rera_id: '',
    // Additional fields
    property_for: '', property_category: '', locality: '',
    floor_type: '', wing: '', special_tags: '',
    property_age: '', has_lift: '', oc_received: '',
    ref_name: '', ref_contact: '', keys_with: '',
    rent_amount: '', rent_deposit: '', maintenance_charges: '', maintenance_unit: '',
    balcony_area: '', terrace_area: '', garden_area: '',
  });

  // Auto-set project_id from query param
  useEffect(() => {
    const qProjectId = searchParams.get('project_id');
    if (qProjectId && !isEditing) {
      setForm(p => ({ ...p, project_id: qProjectId }));
    }
  }, [searchParams, isEditing]);

  // Fetch projects for linkage dropdown — Projects module + Dynamic-Fields list
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('projects?limit=500');
        if (res.success) {
          const list = res.data?.projects || (Array.isArray(res.data) ? res.data : []);
          setProjects(list);
        }
      } catch (err) {}
    })();
    (async () => {
      try {
        const res = await apiFetch('dynamic-fields/projects');
        if (res.success) {
          const list = (Array.isArray(res.data) ? res.data : []).filter(p => p.is_active !== 0);
          setLeadProjects(list);
        }
      } catch (err) {}
    })();
  }, []);

  // Fetch towers when project changes
  useEffect(() => {
    if (form.project_id) {
      (async () => {
        try {
          const res = await apiFetch(`projects/${form.project_id}/towers`);
          if (res.success) setTowers(Array.isArray(res.data) ? res.data : []);
          else setTowers([]);
        } catch (err) { setTowers([]); }
      })();
    } else {
      setTowers([]);
      setForm(p => ({ ...p, tower_id: '' }));
    }
  }, [form.project_id]);

  // Fetch existing property for editing
  useEffect(() => {
    if (isEditing) {
      (async () => {
        try {
          const res = await apiFetch(`properties/${id}`);
          if (res.success && res.data) {
            const p = res.data;
            setForm({
              title: p.title || '', description: p.description || '',
              property_type: p.property_type || '', configuration: p.configuration || '',
              location: p.location || '', address: p.address || '',
              price: p.price || '', price_unit: p.price_unit || 'INR',
              area: p.area || '', area_unit: p.area_unit || 'sqft',
              bedrooms: p.bedrooms ?? '', bathrooms: p.bathrooms ?? '',
              floor: p.floor || '', total_floors: p.total_floors ?? '',
              furnishing: p.furnishing || '', facing: p.facing || '',
              amenities: p.amenities || '', status: p.status || 'available',
              project_id: p.project_id || '', source_lp_id: p.source_lp_id || '',
              unit_number: p.unit_number || '', tower_id: p.tower_id || '',
              carpet_area: p.carpet_area || '', builtup_area: p.builtup_area || '',
              super_builtup_area: p.super_builtup_area || '',
              base_price: p.base_price || '',
              owner_type: p.owner_type || '', owner_name: p.owner_name || '',
              owner_contact: p.owner_contact || '',
              parking: p.parking || '', balconies: p.balconies ?? '',
              rera_id: p.rera_id || '',
              property_for: p.property_for || '', property_category: p.property_category || '',
              locality: p.locality || '', floor_type: p.floor_type || '',
              wing: p.wing || '', special_tags: p.special_tags || '',
              property_age: p.property_age ?? '', has_lift: p.has_lift == null ? '' : String(p.has_lift),
              oc_received: p.oc_received || '',
              ref_name: p.ref_name || '', ref_contact: p.ref_contact || '',
              keys_with: p.keys_with || '',
              rent_amount: p.rent_amount || '', rent_deposit: p.rent_deposit || '',
              maintenance_charges: p.maintenance_charges || '', maintenance_unit: p.maintenance_unit || '',
              balcony_area: p.balcony_area || '', terrace_area: p.terrace_area || '',
              garden_area: p.garden_area || '',
            });
            if (Array.isArray(p.media) && p.media.length > 0) {
              setExistingMedia(p.media);
            }
          }
        } catch (err) {}
      })();
    }
  }, [id, isEditing]);

  const deleteExistingMedia = async (mediaId) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      const res = await apiFetch(`properties/${id}/media/${mediaId}`, { method: 'DELETE' });
      if (res.success) {
        setExistingMedia(prev => prev.filter(m => (m.media_id || m.id) !== mediaId));
        showToast('File deleted');
      } else {
        showToast(res.message || 'Failed to delete', 'error');
      }
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  const setExistingCover = async (mediaId) => {
    try {
      const res = await apiFetch(`properties/${id}/media/${mediaId}/cover`, { method: 'PATCH' });
      if (res.success) {
        setExistingMedia(prev => prev.map(m => ({
          ...m,
          is_cover: (m.media_id || m.id) === mediaId ? 1 : 0,
        })));
        showToast('Cover image set');
      } else {
        showToast(res.message || 'Failed', 'error');
      }
    } catch (err) {
      showToast('Failed', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  // Project dropdown merges two id-spaces (Projects module vs Dynamic-Fields),
  // so option values are tagged `proj:<id>` / `lp:<id>` and split into the
  // matching form field. Only one of project_id / source_lp_id is ever set.
  const projectSelectValue = form.project_id
    ? `proj:${form.project_id}`
    : (form.source_lp_id ? `lp:${form.source_lp_id}` : '');

  const handleProjectSelect = (v) => {
    if (v.startsWith('proj:')) {
      setForm(p => ({ ...p, project_id: v.slice(5), source_lp_id: '' }));
    } else if (v.startsWith('lp:')) {
      setForm(p => ({ ...p, source_lp_id: v.slice(3), project_id: '' }));
    } else {
      setForm(p => ({ ...p, project_id: '', source_lp_id: '' }));
    }
  };

  const projectGroups = useMemo(() => {
    const g = [];
    if (projects.length > 0) {
      g.push({ label: 'Projects', options: projects.map(p => ({ value: `proj:${p.project_id}`, label: p.name })) });
    }
    if (leadProjects.length > 0) {
      g.push({ label: 'Dynamic Fields', options: leadProjects.map(p => ({ value: `lp:${p.lp_id}`, label: p.name })) });
    }
    return g;
  }, [projects, leadProjects]);

  const pricePerSqft = useMemo(() => {
    const price = parseFloat(form.price);
    const carpet = parseFloat(form.carpet_area);
    if (price > 0 && carpet > 0) return Math.round(price / carpet);
    return '';
  }, [form.price, form.carpet_area]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.title.trim()) newErrors.title = 'Title is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) { showToast('Please fix errors', 'error'); return; }

    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.price) payload.price = parseFloat(payload.price);
      if (payload.area) payload.area = parseFloat(payload.area);
      if (payload.bedrooms) payload.bedrooms = parseInt(payload.bedrooms);
      if (payload.bathrooms) payload.bathrooms = parseInt(payload.bathrooms);
      if (payload.total_floors) payload.total_floors = parseInt(payload.total_floors);
      if (payload.carpet_area) payload.carpet_area = parseFloat(payload.carpet_area);
      if (payload.builtup_area) payload.builtup_area = parseFloat(payload.builtup_area);
      if (payload.super_builtup_area) payload.super_builtup_area = parseFloat(payload.super_builtup_area);
      if (payload.base_price) payload.base_price = parseFloat(payload.base_price);
      if (payload.balconies) payload.balconies = parseInt(payload.balconies);
      if (payload.property_age) payload.property_age = parseInt(payload.property_age);
      if (payload.rent_amount) payload.rent_amount = parseFloat(payload.rent_amount);
      if (payload.rent_deposit) payload.rent_deposit = parseFloat(payload.rent_deposit);
      if (payload.maintenance_charges) payload.maintenance_charges = parseFloat(payload.maintenance_charges);
      if (payload.balcony_area) payload.balcony_area = parseFloat(payload.balcony_area);
      if (payload.terrace_area) payload.terrace_area = parseFloat(payload.terrace_area);
      if (payload.garden_area) payload.garden_area = parseFloat(payload.garden_area);
      if (payload.has_lift !== '') payload.has_lift = parseInt(payload.has_lift);
      else payload.has_lift = null;
      if (!payload.project_id) payload.project_id = null;
      if (!payload.source_lp_id) payload.source_lp_id = null;
      if (!payload.tower_id) payload.tower_id = null;
      if (pricePerSqft) payload.price_per_sqft = pricePerSqft;

      const endpoint = isEditing ? `properties/${id}` : 'properties';
      const method = isEditing ? 'PUT' : 'POST';
      const res = await apiFetch(endpoint, { method, body: JSON.stringify(payload) });

      if (res.success) {
        const propertyId = isEditing ? id : (res.data?.prop_id || res.data?.property_id || res.data?.id);

        if (newFiles.length > 0 && propertyId) {
          const formData = new FormData();
          newFiles.forEach(file => formData.append('media', file));
          try {
            await apiFetch(`properties/${propertyId}/media`, {
              method: 'POST',
              body: formData,
            });
          } catch (err) {
            console.error('Media upload error:', err);
          }
        }

        showToast(isEditing ? 'Property updated!' : 'Property created!');
        setTimeout(() => navigate('/properties/all'), 1200);
      } else {
        showToast(res.message || 'Failed to save', 'error');
      }
    } catch (err) {
      showToast('Failed to save', 'error');
    }
    setSaving(false);
  };

  const toggleAmenity = (a) => {
    const current = form.amenities ? form.amenities.split(',').map(s => s.trim()).filter(Boolean) : [];
    const next = current.includes(a) ? current.filter(x => x !== a) : [...current, a];
    setForm(p => ({ ...p, amenities: next.join(', ') }));
  };

  const selectedAmenities = form.amenities ? form.amenities.split(',').map(s => s.trim()).filter(Boolean) : [];
  const showOwnerFields = form.owner_type && form.owner_type !== 'Developer';

  return (
    <div>
      <Header
        title={isEditing ? 'Edit Property' : 'Add Property'}
        subtitle={isEditing ? 'Update property details' : 'List a new property'}
        actions={<Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/properties/all')}>Back</Button>}
      />
      <div className="page">
        <form className="ab-layout" onSubmit={handleSubmit} style={{ maxWidth: 1000 }}>
          <div className="ab-layout__left">
            {/* Basic Info */}
            <CollapsibleSection title="Basic Information" icon={Info}>
              <div className="ab-section__grid">
                <div className="ab-field" style={{ gridColumn: '1/-1' }}>
                  <label className="ab-field__label">Title <span className="ab-field__req">*</span></label>
                  <input className={`ab-field__input ${errors.title ? 'ab-field__input--error' : ''}`} name="title" value={form.title} onChange={handleChange} placeholder="e.g. 2BHK Apartment in Green Valley" />
                  {errors.title && <span className="ab-field__error">{errors.title}</span>}
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Property Type</label>
                  <select className="ab-field__input ab-field__select" name="property_type" value={form.property_type} onChange={handleChange}>
                    <option value="">Select Type</option>
                    {['Apartment', 'Villa', 'Townhouse', 'Penthouse', 'Studio', 'Plot', 'Commercial Office', 'Commercial Shop', 'Warehouse'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Configuration</label>
                  <select className="ab-field__input ab-field__select" name="configuration" value={form.configuration} onChange={handleChange}>
                    <option value="">Select</option>
                    {['Studio', '1 BHK', '1.5 BHK', '2 BHK', '2.5 BHK', '3 BHK', '3.5 BHK', '4 BHK', '4+ BHK', 'Duplex'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Project</label>
                  <SearchableSelect
                    value={projectSelectValue}
                    onChange={handleProjectSelect}
                    groups={projectGroups}
                    placeholder="-- No Project --"
                    clearLabel="-- No Project --"
                  />
                </div>
                {towers.length > 0 && (
                  <div className="ab-field">
                    <label className="ab-field__label">Tower</label>
                    <select className="ab-field__input ab-field__select" name="tower_id" value={form.tower_id} onChange={handleChange}>
                      <option value="">-- Select Tower --</option>
                      {towers.map(t => <option key={t.tower_id || t.id} value={t.tower_id || t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="ab-field">
                  <label className="ab-field__label">Unit Number</label>
                  <input className="ab-field__input" name="unit_number" value={form.unit_number} onChange={handleChange} placeholder="e.g. A-501" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Status</label>
                  <select className="ab-field__input ab-field__select" name="status" value={form.status} onChange={handleChange}>
                    <option value="available">Available</option>
                    <option value="sold">Sold</option>
                    <option value="rented">Rented</option>
                    <option value="reserved">Reserved</option>
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">RERA ID</label>
                  <input className="ab-field__input" name="rera_id" value={form.rera_id} onChange={handleChange} placeholder="e.g. P52100012345" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Property For</label>
                  <select className="ab-field__input ab-field__select" name="property_for" value={form.property_for} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Sale">Sale</option>
                    <option value="Rent">Rent</option>
                    <option value="Lease">Lease</option>
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Property Category</label>
                  <select className="ab-field__input ab-field__select" name="property_category" value={form.property_category} onChange={handleChange}>
                    <option value="">Select</option>
                    {['Apartment', 'Villa', 'Plot', 'Bungalow', 'Row House', 'Penthouse', 'Commercial', 'Office', 'Shop', 'Land'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Wing</label>
                  <input className="ab-field__input" name="wing" value={form.wing} onChange={handleChange} placeholder="e.g. A Wing" />
                </div>
                <div className="ab-field" style={{ gridColumn: '1/-1' }}>
                  <label className="ab-field__label">Special Tags</label>
                  <input className="ab-field__input" name="special_tags" value={form.special_tags} onChange={handleChange} placeholder="e.g. Sea View, Corner Unit, Vastu Compliant" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Property Details */}
            <CollapsibleSection title="Property Details" icon={Home}>
              <div className="ab-section__grid">
                <div className="ab-field">
                  <label className="ab-field__label">Bedrooms</label>
                  <input className="ab-field__input" type="number" name="bedrooms" value={form.bedrooms} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Bathrooms</label>
                  <input className="ab-field__input" type="number" name="bathrooms" value={form.bathrooms} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Balconies</label>
                  <input className="ab-field__input" type="number" name="balconies" value={form.balconies} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Parking</label>
                  <input className="ab-field__input" name="parking" value={form.parking} onChange={handleChange} placeholder="e.g. 1 Covered" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Area</label>
                  <input className="ab-field__input" type="number" name="area" value={form.area} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Area Unit</label>
                  <select className="ab-field__input ab-field__select" name="area_unit" value={form.area_unit} onChange={handleChange}>
                    <option value="sqft">Sq Ft</option>
                    <option value="sqm">Sq Mt</option>
                    <option value="sqyd">Sq Yd</option>
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Carpet Area (sqft)</label>
                  <input className="ab-field__input" type="number" name="carpet_area" value={form.carpet_area} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Built-up Area (sqft)</label>
                  <input className="ab-field__input" type="number" name="builtup_area" value={form.builtup_area} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Super Built-up Area (sqft)</label>
                  <input className="ab-field__input" type="number" name="super_builtup_area" value={form.super_builtup_area} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Floor</label>
                  <input className="ab-field__input" name="floor" value={form.floor} onChange={handleChange} placeholder="e.g. 5th" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Total Floors</label>
                  <input className="ab-field__input" type="number" name="total_floors" value={form.total_floors} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Furnishing</label>
                  <select className="ab-field__input ab-field__select" name="furnishing" value={form.furnishing} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="unfurnished">Unfurnished</option>
                    <option value="semi-furnished">Semi Furnished</option>
                    <option value="fully-furnished">Fully Furnished</option>
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Facing</label>
                  <input className="ab-field__input" name="facing" value={form.facing} onChange={handleChange} placeholder="e.g. North, East" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Floor Type</label>
                  <input className="ab-field__input" name="floor_type" value={form.floor_type} onChange={handleChange} placeholder="e.g. Marble, Vitrified" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Property Age (years)</label>
                  <input className="ab-field__input" type="number" name="property_age" value={form.property_age} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Lift Available</label>
                  <select className="ab-field__input ab-field__select" name="has_lift" value={form.has_lift} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">OC Received</label>
                  <select className="ab-field__input ab-field__select" name="oc_received" value={form.oc_received} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Applied">Applied</option>
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Balcony Area (sqft)</label>
                  <input className="ab-field__input" type="number" name="balcony_area" value={form.balcony_area} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Terrace Area (sqft)</label>
                  <input className="ab-field__input" type="number" name="terrace_area" value={form.terrace_area} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Garden Area (sqft)</label>
                  <input className="ab-field__input" type="number" name="garden_area" value={form.garden_area} onChange={handleChange} placeholder="0" min="0" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Pricing */}
            <CollapsibleSection title="Pricing" icon={DollarSign}>
              <div className="ab-section__grid">
                <div className="ab-field">
                  <label className="ab-field__label">Price</label>
                  <input className="ab-field__input" type="number" name="price" value={form.price} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Base Price</label>
                  <input className="ab-field__input" type="number" name="base_price" value={form.base_price} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Currency</label>
                  <select className="ab-field__input ab-field__select" name="price_unit" value={form.price_unit} onChange={handleChange}>
                    <option value="INR">INR</option>
                    <option value="AED">AED</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Price per Sqft (auto)</label>
                  <input className="ab-field__input" type="text" value={pricePerSqft ? `₹${pricePerSqft.toLocaleString('en-IN')}` : '-'} readOnly disabled style={{ background: 'var(--gray-50)', color: 'var(--gray-500)' }} />
                </div>
              </div>
            </CollapsibleSection>

            {/* Rent & Charges */}
            <CollapsibleSection title="Rent & Charges" icon={DollarSign} defaultOpen={false}>
              <div className="ab-section__grid">
                <div className="ab-field">
                  <label className="ab-field__label">Rent Amount</label>
                  <input className="ab-field__input" type="number" name="rent_amount" value={form.rent_amount} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Rent Deposit</label>
                  <input className="ab-field__input" type="number" name="rent_deposit" value={form.rent_deposit} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Maintenance Charges</label>
                  <input className="ab-field__input" type="number" name="maintenance_charges" value={form.maintenance_charges} onChange={handleChange} placeholder="0" min="0" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Maintenance Unit</label>
                  <select className="ab-field__input ab-field__select" name="maintenance_unit" value={form.maintenance_unit} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>
              </div>
            </CollapsibleSection>

            {/* Ownership */}
            <CollapsibleSection title="Ownership" icon={User}>
              <div className="ab-section__grid">
                <div className="ab-field">
                  <label className="ab-field__label">Owner Type</label>
                  <select className="ab-field__input ab-field__select" name="owner_type" value={form.owner_type} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Developer">Developer</option>
                    <option value="Resale">Resale</option>
                    <option value="Investor">Investor</option>
                    <option value="Broker">Broker</option>
                  </select>
                </div>
                {showOwnerFields && (
                  <>
                    <div className="ab-field">
                      <label className="ab-field__label">Owner Name</label>
                      <input className="ab-field__input" name="owner_name" value={form.owner_name} onChange={handleChange} placeholder="Owner name" />
                    </div>
                    <div className="ab-field">
                      <label className="ab-field__label">Owner Contact</label>
                      <input className="ab-field__input" name="owner_contact" value={form.owner_contact} onChange={handleChange} placeholder="Phone or email" />
                    </div>
                  </>
                )}
                <div className="ab-field">
                  <label className="ab-field__label">Reference Name</label>
                  <input className="ab-field__input" name="ref_name" value={form.ref_name} onChange={handleChange} placeholder="Reference / broker name" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Reference Contact</label>
                  <input className="ab-field__input" name="ref_contact" value={form.ref_contact} onChange={handleChange} placeholder="Phone or email" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Keys With</label>
                  <input className="ab-field__input" name="keys_with" value={form.keys_with} onChange={handleChange} placeholder="e.g. Security, Owner, Office" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Location */}
            <CollapsibleSection title="Location" icon={MapPin}>
              <div className="ab-section__grid">
                <div className="ab-field">
                  <label className="ab-field__label">Location</label>
                  <input className="ab-field__input" name="location" value={form.location} onChange={handleChange} placeholder="e.g. Andheri West, Mumbai" />
                </div>
                <div className="ab-field">
                  <label className="ab-field__label">Locality</label>
                  <input className="ab-field__input" name="locality" value={form.locality} onChange={handleChange} placeholder="e.g. Lokhandwala" />
                </div>
                <div className="ab-field" style={{ gridColumn: '1/-1' }}>
                  <label className="ab-field__label">Address</label>
                  <textarea className="ab-field__input ab-field__textarea" name="address" value={form.address} onChange={handleChange} placeholder="Full address" rows={2} />
                </div>
              </div>
            </CollapsibleSection>

            {/* Description */}
            <CollapsibleSection title="Description" icon={Sparkles}>
              <div className="ab-field">
                <textarea className="ab-field__input ab-field__textarea" name="description" value={form.description} onChange={handleChange} placeholder="Property description, highlights, key selling points..." rows={4} />
              </div>
            </CollapsibleSection>

            {/* Media Upload */}
            <CollapsibleSection title="Media" icon={Camera}>
              {isEditing && existingMedia.length > 0 && (
                <>
                  <p className="addproperty__media-heading">Uploaded files ({existingMedia.length})</p>
                  <div className="addproperty__media-grid">
                    {existingMedia.map(m => {
                      const mediaId = m.media_id || m.id;
                      const url = getFileUrl(m.file_url || m.url || '');
                      const kind = detectKind(m);
                      const isImage = kind === 'image';
                      return (
                        <div className="addproperty__media-card" key={mediaId}>
                          <div className="addproperty__media-thumb">
                            {isImage ? (
                              <img src={url} alt={m.file_name || ''} />
                            ) : kind === 'video' ? (
                              <div className="addproperty__media-icon"><Film size={26} /></div>
                            ) : (
                              <a href={url} target="_blank" rel="noopener noreferrer" className="addproperty__media-icon" onClick={e => e.stopPropagation()}>
                                <FileText size={26} />
                              </a>
                            )}
                            {m.is_cover ? <span className="addproperty__media-cover-badge">Cover</span> : null}
                          </div>
                          <div className="addproperty__media-meta">
                            <span className="addproperty__media-name" title={m.file_name}>{m.file_name || 'File'}</span>
                            <div className="addproperty__media-actions">
                              {isImage && !m.is_cover && (
                                <button type="button" className="addproperty__media-action" title="Set as cover" onClick={() => setExistingCover(mediaId)}>
                                  <Star size={13} />
                                </button>
                              )}
                              <button type="button" className="addproperty__media-action addproperty__media-action--danger" title="Delete" onClick={() => deleteExistingMedia(mediaId)}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              <MediaUploader
                files={newFiles}
                onFilesChange={setNewFiles}
                accept="image/*,application/pdf,video/*"
                maxFiles={15}
                label={isEditing ? 'Add more files' : 'Upload property images, floor plans, or documents'}
              />
            </CollapsibleSection>

            {/* Actions */}
            <div className="ab-actions">
              <Button variant="outline" type="button" onClick={() => navigate('/properties/all')}>Cancel</Button>
              <Button variant="gold" icon={Save} type="submit" disabled={saving}>
                {saving ? 'Saving...' : (isEditing ? 'Update Property' : 'Add Property')}
              </Button>
            </div>
          </div>

          {/* Right Side - Amenities */}
          <div className="ab-layout__right">
            <div className="ab-section ab-section--sticky">
              <h3 className="ab-section__title">Amenities</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {AMENITIES.map(a => (
                  <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--gray-600)', cursor: 'pointer', padding: '4px 0' }}>
                    <input type="checkbox" checked={selectedAmenities.includes(a)} onChange={() => toggleAmenity(a)} style={{ accentColor: 'var(--navy-700)' }} />
                    {a}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProperty;
